import { FC, memo, useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { CheckboxStar, UserUpdaterIndicatorRides } from "@ryde/controls";
import { usePageContext, useRidesNavigation } from "@ryde/redux";
import {
  IRideDto,
  isStarredTableRowIdSF,
  shouldShowUserUpdaterIndicator,
  useFormats,
} from "@ryde/shared";

import { KanbanItemFilterPopover } from "../rides.kanban.item.filter-popover";
import {
  EditKanbanIdLinkStyled,
  KanbanItemHeaderStyled,
} from "../rides.kanban.item.styled";
import { RidesKanbanDragOrSelectHandle } from "./rides.kanban.drag-or-select-handle";

interface IRideKanbanItemHeader {
  dto: IRideDto;
  isDragClone?: boolean;
}

export const RideKanbanItemHeader: FC<IRideKanbanItemHeader> = memo(
  ({ dto, isDragClone }) => {
    const { formatDateNormalized } = useFormats();
    const { entity } = usePageContext();

    const { getRideEditLink } = useRidesNavigation();

    const [isStarred, setIsStarred] = useRecoilState(
      isStarredTableRowIdSF([entity, dto.id]),
    );

    const handleStarredToggle = useEvent((): void => {
      setIsStarred(starred => !starred);
    });

    const rideEditCardLink = useMemo(() => {
      return getRideEditLink({ id: dto.id, keepQuery: true });
    }, [dto.id, getRideEditLink]);

    return (
      <KanbanItemHeaderStyled>
        <RidesKanbanDragOrSelectHandle
          rideDto={dto}
          isDragClone={isDragClone}
        />

        <CheckboxStar
          checked={isStarred}
          onCheckboxToggle={handleStarredToggle}
        />
        <KanbanItemFilterPopover rideDto={dto} />
        <EditKanbanIdLinkStyled to={rideEditCardLink}>
          {dto.id}
        </EditKanbanIdLinkStyled>
        <div className="right">
          {shouldShowUserUpdaterIndicator(dto) && (
            <UserUpdaterIndicatorRides rideDto={dto} displayTimeLabel />
          )}
          {dto.rideStart && (
            <label data-e2e="ride-start">
              {formatDateNormalized(dto.rideStart)}
            </label>
          )}
        </div>
      </KanbanItemHeaderStyled>
    );
  },
);
